<script setup lang="ts">
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { CloseHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { AppSlidePanel, AppLinkButton, AppButton } from '@libs/ui';

import HotelFilters from './HotelFilters.vue';
import type { HotelFilterUpdate } from './model/hotelFilters';

interface HotelFiltersPanelProps {
  isOpen: boolean;
  ingredient: HotelIngredient | OwnStayIngredient | null;
  disableHotelNameFilter?: boolean;
}
const props = defineProps<HotelFiltersPanelProps>();

interface HotelFiltersPanelEmits {
  (eventName: 'close', origin: CloseHotelSelectionFiltersOrigin): void;
  (eventName: 'update-filters', filterUpdated: HotelFilterUpdate): void;
  (eventName: 'apply-filters'): void;
  (eventName: 'reset-filters'): void;
}
const emit = defineEmits<HotelFiltersPanelEmits>();

const browserStore = useBrowserStore();

function onClose(origin: CloseHotelSelectionFiltersOrigin): void {
  emit('close', origin);
}

function onConfirm(): void {
  onClose(CloseHotelSelectionFiltersOrigin.ShowResultsButton);
}
</script>

<template>
  <AppSlidePanel
    :is-open="props.isOpen"
    direction="right"
    :width="!browserStore.isLargerThan1199px ? '100%' : '33.33%'"
    padding="0"
    should-show-close-button
    @close="onClose(CloseHotelSelectionFiltersOrigin.CloseButton)"
    @confirm="onConfirm"
  >
    <template #title>
      {{ $t('filter.preferences') }}
    </template>

    <template #body>
      <div class="hotel-filters-panel-body">
        <HotelFilters
          theme="dark"
          :ingredient="props.ingredient"
          :max-length="25"
          :disable-hotel-name-filter="props.disableHotelNameFilter"
          @update-filter="emit('update-filters', $event)"
        />
      </div>
    </template>
    <template #footer>
      <div class="hotel-filters-panel-footer">
        <AppLinkButton
          class="reset-filters-button"
          :text="$t('filter.reset_preferences')"
          size="large"
          @click="emit('reset-filters')"
        />
        <AppButton
          type="primary"
          state="enabled"
          size="medium"
          @click="emit('apply-filters')"
        >
          {{ $t('filter.apply_preferences') }}
        </AppButton>
      </div>
    </template>
  </AppSlidePanel>
</template>

<style scoped lang="scss">
.hotel-filters-panel-body {
  padding-left: var(--spacing-16);
}
.hotel-filters-panel-footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-16);
}
</style>
